export {};

type CurrentActivity = {
  id: string;
  title: string;
  evaluated: boolean;
  questionCount: number;
};

type PreviousChapter = {
  theme: string;
  chapter: string;
  activityCount: number;
  questionCount: number;
};

type LaunchOptions = {
  currentActivities: CurrentActivity[];
  previousChapters: PreviousChapter[];
  previousQuestionCount: number;
};

type LaunchContext = { classId: string; theme: string; chapter: string; mode: "prerequisite" | "evaluated" };

const nativeFetch = window.fetch.bind(window);
let contextKey = "";
let selectedIds = new Set<string>();
let loadingKey = "";
let renderTimer = 0;

function selectionStorageKey(context: LaunchContext) {
  return `competenceo-qcm-activity-sources:${context.classId}:${context.theme}:${context.chapter}`;
}

function getLaunchCard() {
  return document.querySelector<HTMLElement>(".qcm-launch-card");
}

function readContext(): LaunchContext | null {
  const card = getLaunchCard();
  if (!card) return null;
  const selects = card.querySelectorAll<HTMLSelectElement>(".qcm-form-grid select");
  const classId = selects[0]?.value ?? "";
  const [theme = "", chapter = ""] = (selects[1]?.value ?? "|||").split("|||");
  const activeMode = card.querySelector<HTMLButtonElement>(".qcm-mode-cards button.active");
  const mode = activeMode?.dataset.qcmMode === "evaluated" ? "evaluated" : "prerequisite";
  if (!classId || !chapter) return null;
  return { classId, theme, chapter, mode };
}

function contextSignature(context: LaunchContext) {
  return `${context.classId}|||${context.theme}|||${context.chapter}|||${context.mode}`;
}

function restoreSelection(context: LaunchContext, activities: CurrentActivity[]) {
  try {
    const raw = localStorage.getItem(selectionStorageKey(context));
    if (raw) {
      const saved = JSON.parse(raw);
      if (Array.isArray(saved)) return new Set(saved.map(String).filter((id) => activities.some((item) => item.id === id)));
    }
  } catch { /* ignore */ }
  return new Set(activities.filter((item) => item.evaluated).map((item) => item.id));
}

function persistSelection(context: LaunchContext) {
  try { localStorage.setItem(selectionStorageKey(context), JSON.stringify([...selectedIds])); }
  catch { /* ignore */ }
}

function ensurePanel() {
  const card = getLaunchCard();
  if (!card) return null;
  let panel = card.querySelector<HTMLElement>("[data-qcm-activity-filter]");
  if (panel) return panel;
  panel = document.createElement("section");
  panel.dataset.qcmActivityFilter = "true";
  panel.className = "qcm-activity-source-panel";
  const countGrid = card.querySelector(".qcm-count-grid");
  const anchor = countGrid ?? card.querySelector(".qcm-count-row") ?? card.querySelector(".qcm-launch-summary");
  if (anchor) anchor.insertAdjacentElement("afterend", panel);
  else card.appendChild(panel);
  return panel;
}

function relabelExistingControls() {
  const card = getLaunchCard();
  if (!card) return;
  const blocks = [...card.querySelectorAll<HTMLElement>(".qcm-count-grid label")];
  if (blocks[1]) {
    const title = blocks[1].querySelector("span");
    const small = blocks[1].querySelector("small");
    if (title) title.textContent = "Questions des activités cochées";
    if (small) small.textContent = "Uniquement parmi les activités sélectionnées ci-dessous";
  }
  if (blocks[2]) {
    const title = blocks[2].querySelector("span");
    const small = blocks[2].querySelector("small");
    if (title) title.textContent = "Révisions de chapitres précédents";
    if (small) small.textContent = "Sources automatiques : activités déjà évaluées avec cette classe";
  }
}

function renderOptions(panel: HTMLElement, context: LaunchContext, options: LaunchOptions) {
  selectedIds = restoreSelection(context, options.currentActivities);
  const currentList = options.currentActivities.length
    ? options.currentActivities.map((activity) => `<label class="qcm-activity-source-item ${activity.questionCount ? "" : "no-questions"}">
        <input type="checkbox" value="${activity.id}" ${selectedIds.has(activity.id) ? "checked" : ""} ${activity.questionCount ? "" : "disabled"}>
        <span><strong>${escapeHtml(activity.title)}</strong><small>${activity.questionCount} QCM validé${activity.questionCount > 1 ? "s" : ""}${activity.evaluated ? " · déjà évaluée" : ""}</small></span>
      </label>`).join("")
    : `<div class="qcm-activity-source-empty">Aucune activité n’est enregistrée pour ce chapitre.</div>`;

  const previousList = options.previousChapters.length
    ? options.previousChapters.map((item) => `<div class="qcm-previous-chapter-item"><span>${escapeHtml(item.theme ? `${item.theme} · ${item.chapter}` : item.chapter)}</span><small>${item.activityCount} activité${item.activityCount > 1 ? "s" : ""} évaluée${item.activityCount > 1 ? "s" : ""} · ${item.questionCount} QCM disponible${item.questionCount > 1 ? "s" : ""}</small></div>`).join("")
    : `<div class="qcm-activity-source-empty">Aucun ancien chapitre n’est encore éligible pour cette classe.</div>`;

  panel.innerHTML = `<div class="qcm-activity-source-block">
      <div class="qcm-activity-source-head"><div><strong>Activités du chapitre en cours</strong><span>Coche uniquement les activités déjà faites avec cette classe.</span></div><div><button type="button" data-qcm-select-all>Tout cocher</button><button type="button" data-qcm-clear-all>Tout décocher</button></div></div>
      <div class="qcm-activity-source-list">${currentList}</div>
    </div>
    <div class="qcm-activity-source-block previous">
      <div class="qcm-activity-source-head"><div><strong>Chapitres précédents</strong><span>Automatique : seulement les activités qui ont déjà reçu au moins une évaluation avec cette classe.</span></div><b>${options.previousQuestionCount} QCM éligible${options.previousQuestionCount > 1 ? "s" : ""}</b></div>
      <div class="qcm-previous-chapter-list">${previousList}</div>
    </div>`;

  panel.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((input) => input.addEventListener("change", () => {
    if (input.checked) selectedIds.add(input.value); else selectedIds.delete(input.value);
    persistSelection(context);
  }));
  panel.querySelector<HTMLButtonElement>("[data-qcm-select-all]")?.addEventListener("click", () => {
    selectedIds = new Set(options.currentActivities.filter((item) => item.questionCount > 0).map((item) => item.id));
    panel.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:not(:disabled)').forEach((input) => { input.checked = true; });
    persistSelection(context);
  });
  panel.querySelector<HTMLButtonElement>("[data-qcm-clear-all]")?.addEventListener("click", () => {
    selectedIds.clear();
    panel.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:not(:disabled)').forEach((input) => { input.checked = false; });
    persistSelection(context);
  });
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[character]!));
}

async function refreshPanel() {
  if (getLaunchCard()?.dataset.akyo === "true") return;
  relabelExistingControls();
  const panel = ensurePanel();
  const context = readContext();
  if (!panel || !context) return;
  panel.hidden = context.mode !== "evaluated";
  if (context.mode !== "evaluated") return;
  const signature = contextSignature(context);
  if (signature === contextKey && panel.dataset.loaded === "true") return;
  if (loadingKey === signature) return;
  loadingKey = signature;
  contextKey = signature;
  panel.dataset.loaded = "false";
  panel.innerHTML = `<div class="qcm-activity-source-loading">Chargement des activités autorisées…</div>`;
  try {
    const params = new URLSearchParams({ classId: context.classId, theme: context.theme, chapter: context.chapter });
    const response = await nativeFetch(`/api/qcm/admin/launch-options?${params.toString()}`, { credentials: "include", cache: "no-store" });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error ?? "Chargement impossible");
    if (contextSignature(readContext() ?? context) !== signature) return;
    renderOptions(panel, context, body as LaunchOptions);
    panel.dataset.loaded = "true";
  } catch (reason) {
    panel.innerHTML = `<div class="qcm-activity-source-error">${escapeHtml(reason instanceof Error ? reason.message : "Chargement impossible")}</div>`;
  } finally {
    if (loadingKey === signature) loadingKey = "";
  }
}

window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const rawUrl = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
  const pathname = (() => { try { return new URL(rawUrl, window.location.origin).pathname; } catch { return rawUrl; } })();
  const isLiveCreate = pathname === "/api/qcm/admin/sessions";
  const isSecureCreate = pathname === "/api/secure-qcm/admin/sessions";
  if ((isLiveCreate || isSecureCreate) && String(init?.method ?? "GET").toUpperCase() === "POST" && typeof init?.body === "string") {
    try {
      const body = JSON.parse(init.body);
      if (body?.akyo) return nativeFetch(input, init);
      if (body?.mode === "evaluated") {
        const context = readContext();
        const stored = context ? restoreSelection(context, []) : new Set<string>();
        const ids = selectedIds.size ? [...selectedIds] : [...stored];
        const nextBody = { ...body, filters: { ...(body.filters ?? {}), currentActivityIds: ids } };
        const nextUrl = isLiveCreate ? "/api/qcm/admin/sessions-filtered" : "/api/secure-qcm/admin/sessions-filtered";
        return nativeFetch(nextUrl, { ...init, body: JSON.stringify(nextBody) });
      }
    } catch { /* laisse le lancement normal traiter les payloads inattendus */ }
  }
  return nativeFetch(input, init);
};

function scheduleRefresh() {
  window.clearTimeout(renderTimer);
  renderTimer = window.setTimeout(() => void refreshPanel(), 40);
}

document.addEventListener("change", (event) => {
  const target = event.target;
  if (target instanceof HTMLSelectElement && target.closest(".qcm-launch-card")) {
    contextKey = "";
    scheduleRefresh();
  }
});
document.addEventListener("click", (event) => {
  const target = event.target instanceof Element ? event.target.closest(".qcm-mode-cards button") : null;
  if (target) { contextKey = ""; window.setTimeout(scheduleRefresh, 0); }
});

const activitySourceObserver = new MutationObserver(scheduleRefresh);
activitySourceObserver.observe(document.documentElement, { childList: true, subtree: true });
window.setTimeout(scheduleRefresh, 0);
